<script setup lang="ts" generic="T extends { id: number | string }">
import type { PageState, PageStatus } from "@/composables/useInfiniteList";

const props = withDefaults(
  defineProps<{
    pageStates: PageState<T>[];
    firstPageStatus: PageStatus;
    total: number;
    totalPages: number;
    generation: number;
    registerContainer: (el: unknown, page: number) => void;
    setTopRef?: (el: unknown) => void;
    stickyCount?: boolean;
    tileHeightFallback?: number;
  }>(),
  {
    setTopRef: () => {},
    stickyCount: false,
    tileHeightFallback: 120,
  }
);

const emit = defineEmits<{
  load: [page: number];
  retry: [page: number];
}>();

defineSlots<{
  default(props: { item: T }): unknown;
  count(): unknown;
  skeleton(): unknown;
  footer(): unknown;
  empty(): unknown;
}>();

const { t } = useI18n();

// Reserved height per container = count × avgTileHeight, refined from each rendered page (D3).
const containerEls = new Map<number, HTMLElement>();
const measuredPages = new Set<number>();
const heightSum = ref(0);
const sampleCount = ref(0);
const avgTileHeight = computed(() => (sampleCount.value > 0 ? heightSum.value / sampleCount.value : props.tileHeightFallback));

function onContainerRef(el: unknown, page: number) {
  props.registerContainer(el, page);
  if (el instanceof HTMLElement) containerEls.set(page, el);
  else containerEls.delete(page);
}

function measureRenderedPages() {
  if (typeof requestAnimationFrame === "undefined") return;
  requestAnimationFrame(() => {
    for (const ps of props.pageStates) {
      if (ps.status !== "success" || ps.items.length === 0 || measuredPages.has(ps.page)) continue;
      const el = containerEls.get(ps.page);
      if (!el) continue;
      // Never measure a content-visibility-skipped container: offsetHeight would return the
      // reserved estimate and poison the average. CV:auto re-measures it on its first real paint.
      if (typeof el.checkVisibility === "function" && !el.checkVisibility({ contentVisibilityAuto: true })) continue;
      const height = el.offsetHeight;
      if (height <= 0) continue;
      heightSum.value += height;
      sampleCount.value += ps.items.length;
      measuredPages.add(ps.page);
    }
  });
}

const statusSignal = computed(() => props.pageStates.map((p) => `${p.page}:${p.status}`).join(","));
watch(statusSignal, () => measureRenderedPages(), { flush: "post" });
watch(
  () => props.generation,
  () => measuredPages.clear()
);

function reserveStyle(ps: PageState<T>): string {
  const reserved = `${Math.round(ps.count * avgTileHeight.value)}px`;
  // CV:auto only on loaded containers (heavy real tiles); idle/pending/error stay rendered so the
  // sr-only load button keeps its place in the tab order (AC5).
  if (ps.status === "success") return `content-visibility:auto;contain-intrinsic-size:auto ${reserved}`;
  return `min-height:${reserved}`;
}

// Focus moves to the new tiles only on a manual sr-only load, never on observer/scroll loads (D5).
const manualPage = ref<number | null>(null);
function onManualLoad(page: number) {
  manualPage.value = page;
  emit("load", page);
}
watch(statusSignal, () => {
  const page = manualPage.value;
  if (page == null) return;
  if (props.pageStates.find((p) => p.page === page)?.status !== "success") return;
  manualPage.value = null;
  nextTick(() => {
    const el = containerEls.get(page);
    const focusable = el?.querySelector<HTMLElement>('a, button, [tabindex]:not([tabindex="-1"])');
    focusable?.focus();
  });
});

const liveMessage = computed(() => {
  if (props.firstPageStatus === "pending") return t("ui.loading");
  if (props.total === 0) return "";
  return t("ui.list.itemsLoaded", props.total);
});

const isEmpty = computed(() => props.firstPageStatus === "success" && props.total === 0);
const rowClass = "not-last:border-b border-lagoon-300";
</script>

<template>
  <p role="status" class="sr-only">{{ liveMessage }}</p>

  <WebErrorRetry v-if="firstPageStatus === 'error'" class="px-4" @retry="emit('retry', 1)" />

  <div v-else>
    <div :ref="setTopRef" aria-hidden="true" class="scroll-mt-(--ui-header-height)" />

    <AppResultCount
      v-if="$slots.count"
      :loading="firstPageStatus === 'pending'"
      class="max-lg:hidden text-primary bg-white px-4 sm:px-10 py-4 border-b border-lagoon-300"
      :class="{ 'sticky z-1 top-(--ui-header-height)': stickyCount && !isEmpty }"
    >
      <slot name="count" />
    </AppResultCount>

    <slot v-if="isEmpty" name="empty" />

    <ol v-else role="list" :aria-busy="firstPageStatus === 'pending'" class="not-last:border-b border-lagoon-300">
      <li
        v-for="(ps, index) in pageStates"
        :key="`${generation}-${ps.page}`"
        :ref="(el) => onContainerRef(el, ps.page)"
        :style="reserveStyle(ps)"
        :class="{ '[&_li:last-child]:border-b-0': index === pageStates.length - 1 }"
      >
        <ul v-if="ps.status === 'success'" role="list" :aria-label="t('ui.list.page', { page: ps.page })">
          <li v-for="item in ps.items" :key="item.id" :class="rowClass">
            <slot :item="item" />
          </li>
        </ul>

        <template v-else-if="ps.status === 'pending'">
          <div v-for="i in ps.count" :key="i" :class="rowClass" aria-hidden="true">
            <slot name="skeleton" />
          </div>
        </template>

        <WebErrorRetry v-else-if="ps.status === 'error'" class="px-4" @retry="emit('retry', ps.page)" />

        <button v-else type="button" class="sr-only focus:not-sr-only focus:block focus:w-full focus:py-4" @click="onManualLoad(ps.page)">
          {{ t("ui.list.loadPage", { page: ps.page }) }}
        </button>
      </li>
    </ol>

    <slot name="footer" />
  </div>

  <div aria-hidden="true" class="h-(--bottom-padding,0)" />
</template>
